import React, { useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CreateOutlined from "@mui/icons-material/CreateOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OperatingHoursPopup from "../operating-hours-popup";
import BasicInformationForm from "../basic-information-form";
import "./basic-information.scss";

interface BasicInformationProps {
  data: any;
  refresh?: () => void;
}

const DAYS_ORDER = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const BasicInformation: React.FC<BasicInformationProps> = ({
  data,
  refresh,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const formikRef = useRef<any>(null);

  const [localOperationalHours, setLocalOperationalHours] = useState(
    data?.operationalHours,
  );

  React.useEffect(() => {
    setLocalOperationalHours(data?.operationalHours);
  }, [data?.operationalHours, editMode]);

  const operationalHours = localOperationalHours;
  const hasOperatingHours =
    !!operationalHours &&
    Object.values(operationalHours.days || {}).some(Boolean);

  const handleOperatingHoursSave = (newHours: any) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("operationalHours", newHours);
    }
    setLocalOperationalHours(newHours);
    setIsDialogOpen(false);
  };


  return (
    <Box className="basic-information">
      <Box className="basic-information__info-form">
        <BasicInformationForm
          data={data}
          editMode={editMode}
          onEditModeChange={setEditMode}
          innerRef={formikRef}
          refresh={refresh}
        />
      </Box>
      <Box className="basic-information__top-box" />
      {/* Operating Hours Box */}
      <Box className="basic-information__operating-hours">
        {/* Header */}
        <Box className="basic-information__header-row">
          <Box className="basic-information__header-text">
            <Typography variant="h6" className="basic-information__title">
              Operating Hours
            </Typography>
            <Typography variant="body2" className="basic-information__subtitle">
              Define your service availability by selecting dates and time slots
            </Typography>
          </Box>
          {hasOperatingHours && editMode && (
            <CreateOutlined
              className="basic-information__edit-icon"
              fontSize="small"
              onClick={() => setIsDialogOpen(true)}
            />
          )}
        </Box>

        {/* EMPTY STATE */}
        {!hasOperatingHours && (
          <>
            {editMode && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                className="basic-information__add-button"
                onClick={() => setIsDialogOpen(true)}
              >
                Add Operating Hours
              </Button>
            )}

            <Box className="basic-information__empty-state">
              <CalendarTodayIcon
                className="basic-information__empty-icon"
                fontSize="large"
              />
              <Typography
                variant="body2"
                className="basic-information__empty-text"
              >
                No operating hours added yet.
                {editMode && ' Click "Add Operating Hours" to get started.'}
              </Typography>
            </Box>
          </>
        )}

        {/* SELECTED OPERATING HOURS */}
        {hasOperatingHours && (
          <Box className="basic-information__hours-box">
            <Box className="basic-information__day-row-box">
              {/* Active Days Group */}
              <Box className="basic-information__day-left-box">
                <span className="basic-information__status-dot is-open" />
                <Typography className="basic-information__day-name-box">
                  {DAYS_ORDER.filter((day) => operationalHours.days?.[day])
                    .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
                    .join(", ")}
                </Typography>
                <Box display="flex" alignItems="center">
                  <AccessTimeIcon
                    fontSize="small"
                    className="basic-information__time-icon"
                  />
                  <Typography sx={{ fontSize: "14px", fontWeight: 800 }}>
                    {operationalHours.openingTime?.replace(" to ", " - ") ||
                      "9:00 AM - 5:00 PM"}
                  </Typography>
                </Box>
              </Box>

              {/* Closed Days Group */}
              <Box className="basic-information__day-left-box" sx={{ mt: 1 }}>
                <span className="basic-information__status-dot is-closed" />
                <Typography className="basic-information__day-name-box">
                  {(() => {
                    const days = DAYS_ORDER.filter(
                      (day) => !operationalHours.days?.[day],
                    ).map(
                      (day) => day.charAt(0).toUpperCase() + day.slice(1),
                    );

                    return days.length ? days.join(", ") : "No closed days";
                  })()}
                </Typography>
                <Typography className="basic-information__closed-text">
                  Closed
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {/* Operating Hours Popup */}
      <OperatingHoursPopup
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleOperatingHoursSave}
        initialData={
          formikRef.current?.values?.operationalHours || operationalHours
        }
      />
    </Box>
  );
};

export default BasicInformation;
